import React from 'react'
import { Carousel, CarouselSlide as Slide, useContainerWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { i18n } from '@fs/zion-locale'
import { format } from '@fs/zion-locale/date-fns'
import image0 from './images/coverSlide/image.jpg'
import image1 from './images/1894/image.jpg'
import imageBlur1 from './images/1894/blur-image.jpg'
import image2 from './images/1924/image.jpg'
import imageBlur2 from './images/1924/blur-image.jpg'
import image3 from './images/1938/image.jpg'
import imageBlur3 from './images/1938/blur-image.jpg'
import image4 from './images/1964/image.jpg'
import imageBlur4 from './images/1964/blur-image.jpg'
import image5 from './images/1999/image.jpg'
import imageBlur5 from './images/1999/blur-image.jpg'
import image6 from './images/2002/image.jpg'
import imageBlur6 from './images/2002/blur-image.jpg'
import image7 from './images/2011/image.jpg'
import imageSmall7 from './images/2011/image.small.jpg'
import imageMedium7 from './images/2011/image.medium.jpg'
import imageBlur7 from './images/2011/blur-image.jpg'
import image8 from './images/2013/image.jpg'
import imageMedium8 from './images/2013/image.medium.jpg'
import imageBlur8 from './images/2013/blur-image.jpg'
import image9 from './images/2014/image.jpg'
import imageBlur9 from './images/2014/blur-image.jpg'

const smallScreenHeight = css`
  height: 448px;
`
const otherScreenHeight = css`
  height: 650px;
`
const croatianException = i18n.language === 'hr' ? '.' : ''
// the 'Mon Jan 1' in each of these dates is to keep it on the year specified
const FoundingFamilysearchYear = `${format(new Date('Mon Jan 1 1894'), 'yyyy')}${croatianException}`
const researchHelpMultipleLanguagesYear = `${format(new Date('Mon Jan 1 1924'), 'yyyy')}${croatianException}`
const microfilmingStartsYear = `${format(new Date('Mon Jan 1 1938'), 'yyyy')}${croatianException}`
const firstLocalFamilyHistoryCenterYear = `${format(new Date('Mon Jan 1 1964'), 'yyyy')}${croatianException}`
const websiteGoesLiveYear = `${format(new Date('Mon Jan 1 1999'), 'yyyy')}${croatianException}`
const firstSearchableRecordsYear = `${format(new Date('Mon Jan 1 2002'), 'yyyy')}${croatianException}`
const firstRootstechConferenceYear = `${format(new Date('Mon Jan 1 2011'), 'yyyy')}${croatianException}`
const billionIndexedRecordsYear = `${format(new Date('Mon Jan 1 2013'), 'yyyy')}${croatianException}`
const billionHistoricalRecordsYear = `${format(new Date('Mon Jan 1 2014'), 'yyyy')}${croatianException}`

export default function AboutPageCarousel() {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()

  return (
    <div css={atWidth({ default: smallScreenHeight, sm: otherScreenHeight })}>
      <Carousel>
        <Slide
          fullScrim
          src={image0}
          alt=""
          heading={t('about.our-history.heading', 'Our History')}
          subHeading={t(
            'about.our-history.body',
            'We started as a small society of people working to find their ancestors. As technology grew the society grew into a larger organization. Here is how it all happened.'
          )}
        />
        <Slide
          src={image1}
          srcBlur={imageBlur1}
          alt=""
          overline={t('about.founding-of-familysearch.heading', 'Founding of FamilySearch')}
          heading={FoundingFamilysearchYear}
          subHeading={t(
            'about.founding-of-familysearch.body',
            'The Genealogical Society of Utah, now FamilySearch, was founded to help Church members trace their family trees.'
          )}
        />
        <Slide
          src={image2}
          srcBlur={imageBlur2}
          alt=""
          overline={t('about.research-help-in-multiple-languages.heading', 'Research Help in Multiple Languages')}
          heading={researchHelpMultipleLanguagesYear}
          subHeading={t(
            'about.research-help-in-multiple-languages.body',
            'What started locally supporting a handful of languages nearly 100 years ago has blossomed into a website that has users from 238 countries.'
          )}
        />
        <Slide
          src={image3}
          srcBlur={imageBlur3}
          alt=""
          overline={t('about.microfilm-starts.heading', 'Microfilming Starts')}
          heading={microfilmingStartsYear}
          subHeading={t(
            'about.microfilm-starts.body',
            'Filming of paper documents began in church and government archives. Microfilm was used until 2002, when capture became 100% digital.'
          )}
          attribution={i18n.language === 'en' && t('about.carousel.attribution', 'Photo provided by Nelson W. Clayton')}
        />
        <Slide
          src={image4}
          srcBlur={imageBlur4}
          alt=""
          overline={t('about.first-local-family-history-center.heading', 'First Local Family History Center')}
          heading={firstLocalFamilyHistoryCenterYear}
          subHeading={t(
            'about.first-local-family-history-center.body',
            'The first local family history center opens. FamilySearch centers currently provide in-person research help at more than 5,000 locations worldwide.'
          )}
        />
        <Slide
          src={image5}
          srcBlur={imageBlur5}
          alt=""
          overline={t('about.website-goes-live.heading', 'Website Goes Live')}
          heading={websiteGoesLiveYear}
          subHeading={t(
            'about.website-goes-live.body',
            'The FamilySearch website launched and quickly became the most popular family history website on the internet with 7 million hits per day. In 2020, our average visitors per day is 400,000.'
          )}
        />
        <Slide
          src={image6}
          srcBlur={imageBlur6}
          alt=""
          overline={t('about.first-searchable-records.heading', 'First Searchable Records')}
          heading={firstSearchableRecordsYear}
          subHeading={t(
            'about.first-searchable-records.body',
            'The 1880 census, listing United States residents in 1880, becomes the first publicly searchable record collection on FamilySearch.org.'
          )}
        />
        <Slide
          src={atWidth({ default: imageSmall7, md: imageMedium7, xxl: image7 })}
          srcBlur={imageBlur7}
          alt=""
          overline={t('about.first-rootstech-conference.heading', 'First Rootstech Conference')}
          heading={firstRootstechConferenceYear}
          subHeading={t(
            'about.first-rootstech-conference.body',
            'RootsTech debuts in Salt Lake City. Over the years it has become the largest genealogy and family history conference in the world.'
          )}
        />
        <Slide
          src={atWidth({ default: imageMedium8, xxl: image8 })}
          srcBlur={imageBlur8}
          alt=""
          overline={t('about.one-billion-indexed-records.heading', '1 Billion Indexed Records')}
          heading={billionIndexedRecordsYear}
          subHeading={t(
            'about.one-billion-indexed-records.body',
            'FamilySearch.org tops 1 billion records indexed thanks to hundreds of thousands of indexing volunteers. Five years later we crossed 2 billion.'
          )}
        />
        <Slide
          src={image9}
          srcBlur={imageBlur9}
          alt=""
          overline={t('about.one-billion-historical-records.heading', '1 Billion Historical Records')}
          heading={billionHistoricalRecordsYear}
          subHeading={t(
            'about.one-billion-historical-records.body',
            'FamilySearch.org publishes its 1 billionth image of a historical record. Site guests use records to learn the details of their family story.'
          )}
        />
      </Carousel>
    </div>
  )
}
